import { useMemo, useState } from 'react';
import {
	COMPARABLE_VERSION_STATUSES,
	WORKING_COPY_VALUE,
	resolveVersionSchema,
} from './compare-constants';
import { buildComparison } from './comparison-model';

function buildOptions(versions, statuses, workingCopy) {
	const opts = [];
	if (workingCopy) {
		opts.push({ value: WORKING_COPY_VALUE, label: workingCopy.label || 'Working copy', status: 'DRAFT' });
	}
	for (const v of versions) {
		if (!statuses.includes(v.status)) continue;
		opts.push({ value: v.version, label: `v${v.version}`, status: v.status });
	}
	return opts;
}

function defaultBase(versions, statuses) {
	const active = versions.find(v => v.status === 'ACTIVE' && statuses.includes(v.status));
	if (active) return active.version;
	const first = versions.find(v => statuses.includes(v.status));
	return first ? first.version : null;
}

/**
 * Selection + comparison state for the Compare UI.
 * @param {{ versions: object[], workingCopy?: {label,schema}, statuses?: string[],
 *           initialBase?: string, initialTarget?: string, initialOnlyChanges?: boolean }} input
 */
export default function useVersionComparison({ versions = [], workingCopy = null, statuses = COMPARABLE_VERSION_STATUSES, initialBase, initialTarget, initialOnlyChanges = true } = {}) {
	const options = useMemo(() => buildOptions(versions, statuses, workingCopy), [versions, statuses, workingCopy]);

	const resolvedDefaultBase = initialBase || defaultBase(versions, statuses);
	const resolvedDefaultTarget = initialTarget
		|| (workingCopy ? WORKING_COPY_VALUE : (versions[0] ? versions[0].version : null));

	const [base, setBase] = useState(resolvedDefaultBase);
	const [target, setTarget] = useState(resolvedDefaultTarget);
	const [onlyChanges, setOnlyChanges] = useState(initialOnlyChanges);

	const resolveByValue = (value) => {
		if (value === WORKING_COPY_VALUE) return workingCopy ? workingCopy.schema : null;
		const v = versions.find(x => x.version === value);
		return v ? resolveVersionSchema(v) : null;
	};

	const resolveVersionRecord = (value) => {
		if (value === WORKING_COPY_VALUE) {
			return workingCopy ? { isWorkingCopy: true, label: workingCopy.label, status: 'DRAFT' } : null;
		}
		return versions.find(x => x.version === value) || null;
	};

	const leftSchema = resolveByValue(base);
	const rightSchema = resolveByValue(target);

	const baseVersion = resolveVersionRecord(base);
	const targetVersion = resolveVersionRecord(target);

	const comparison = useMemo(
		() => buildComparison({ leftSchema, rightSchema }),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[base, target, versions, workingCopy],
	);

	const sameSide = Boolean(base && target && base === target);

	return {
		options,
		base,
		target,
		setBase,
		setTarget,
		onlyChanges,
		setOnlyChanges,
		leftSchema,
		rightSchema,
		comparison,
		sameSide,
		baseVersion,
		targetVersion,
	};
}
